import React from 'react';
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { getAttributeCreatorFor, Types, Icons } from '@leverege/api-attributes'
import { Label, Checkbox, Button, Selector } from '@leverege/ui-elements'

import { 
  TextInput, Flex, LabelGroup, Section, Tags, Icon, 
  FontIconSelector, ColorSelector, JsonEditorDialog, InsetSection,
} from '~/views' // eslint-disable-line

import style from './style.css'

export default class BaseAttributeEditor extends React.Component {

  static propTypes = {
    attribute : PropTypes.object,
    blueprint : PropTypes.object,
    currentTags : PropTypes.array,
    onChange : PropTypes.func.isRequired
  } 

  constructor( props ) {
    super( props )
    this.state = { showJsonEditor : false }
    this.changePath = BaseAttributeEditor.ChangePath
  }

  static ChangePath( path, value, err ) {
    const { onChange, attribute } = this.props 

    if ( err ) {
      console.error( `Error setting ${value} at path ${path} due to error: ${err.message}` )
      return
    }
    // eslint-disable-next-line no-unused-expressions
    onChange?.( { target : attribute, path, value } ) 
  }

  /**
   * This will convert an updater change into an attribute change and 
   * send it on this.changePath( event )
   **/
  static OnUpdaterChange( event ) {
    this.changePath( `sim/updater/${event.path}`, event.value )
  }

  onFontSelected = font => this.changePath( 'icon/font', font )

  onColorSelected = color => this.changePath( 'icon/fontColor', color.rgb )

  render() {
    const { showJsonEditor } = this.state 
    const { attribute, typeName, currentTags } = this.props 
    
    if ( attribute == null ) {
      return <div />
    }

    const attrCreator = getAttributeCreatorFor( attribute.type );
    const attrIcon = Icons.getIcon( attribute )

    let stringifiedMetadata
    if ( attribute.metadata ) {
      stringifiedMetadata = JSON.stringify( attribute.metadata, null, 2 )
    }

    const popover = (
      <Popover
        className={style.popover}
        style={{ minWidth : '300px' }}
        id="icon"
        title="Set Icon URL">
        <LabelGroup>
          <label>URL</label>
          <TextInput
            hint="Icon URL..."
            value={attribute.icon?.url} 
            changeOnReturn 
            onChange={( v, err ) => { this.changePath( 'icon/url', v, err ) }} />
        </LabelGroup> 
        <div style={{ textAlign : 'center' }}>OR</div>
        <div className={style.fontOptions}>
          <LabelGroup>
            <label>Font</label>
            <FontIconSelector style={{ marginRight : '10px' }} onSelect={this.onFontSelected} font={attrIcon?.fontClass} />
            <ColorSelector onChangeComplete={this.onColorSelected} />
          </LabelGroup>
        </div>
      </Popover>
    )

    const icon = (
      <OverlayTrigger key="icon" rootClose trigger="click" placement="left" overlay={popover}>
        <div className={classnames( style.iconHolder, attrIcon && style.hasIcon )} style={{ width : '40px', height : '40px' }}>
          {attrIcon == null ? 
            <div className={style.noIcon}>SET ICON</div> : 
            <Icon className={style.icon} icon={attrIcon} />}
        </div>
      </OverlayTrigger>
    )

    return (
      <Section title={typeName || attrCreator?.name?.()} subtitle={attribute.path} titleOnTop right={icon}>
        <Flex variant="vSpace" align="stretch" direction="column">

          <LabelGroup>
            <Label>Name</Label>
            <TextInput
              value={attribute.name} 
              changeOnReturn
              size={10}
              onChange={( v, err ) => { this.changePath( 'name', v, err ) }} />
          </LabelGroup>

          <LabelGroup>
            <Label>Description</Label>
            <TextInput
              value={attribute.desc}
              changeOnReturn
              size={10}
              onChange={( v, err ) => { this.changePath( 'desc', v, err ) }} />
          </LabelGroup>

          <LabelGroup>
            <Label>Tags</Label>
            <Tags
              tags={attribute.tags}
              autofocus={false}
              suggestions={currentTags}
              tagAdded={( tag, arr ) => { this.changePath( 'tags', arr, false ) }}
              tagRemoved={( tag, arr ) => { this.changePath( 'tags', arr, false ) }} />
          </LabelGroup>

          <Flex variant="noSpace" align="center">
            <LabelGroup style={{ flexBasis : '200px', marginRight : '8px' }}>
              <Label />
              <Flex direction="row">
                <Checkbox
                  className={style.checkbox}
                  value={attribute.searchable} 
                  onChange={( evt ) => { this.changePath( 'searchable', evt.value, false ) }} />
                Searchable
              </Flex>
            </LabelGroup>
          </Flex>

          <Flex variant="noSpace">
            <LabelGroup style={{ flexBasis : '200px', marginRight : '8px' }}>
              <Label>Source</Label>
              <Selector
                values={Types.Source.values()}
                value={attribute.source}
                onChange={( evt ) => { console.log( evt ); this.changePath( 'source', evt.value, false ); }} />
            </LabelGroup>

            <Flex direction="row">
              <Checkbox
                value={attribute.controllable} 
                onChange={( evt ) => { this.changePath( 'controllable', evt.value, false ) }} />
              Controllable
            </Flex>

          </Flex>

          <LabelGroup>
            <Label>Metadata</Label>
            <Flex>
              <Button 
                variant="secondarySmall"
                className={style.addMetadataButton}
                onClick={() => this.setState( { showJsonEditor : true } )}>
                {stringifiedMetadata ? 'Edit' : 'Add'}
              </Button>
            </Flex>
          </LabelGroup>
          
          <InsetSection style={{ width : '100%' }}>
            <div className={stringifiedMetadata ? style.metadata : style.noMetadata}>
              {stringifiedMetadata || 'No Metadata' }
            </div> 
          </InsetSection>

          <JsonEditorDialog 
            title="Attribute Metadata"
            message="Enter the JSON object for the attribute metadata"
            show={showJsonEditor}
            onOk={( metadata ) => { 
              this.changePath( 'metadata', metadata, false )
              this.setState( { showJsonEditor : false } ) 
            }}
            onCancel={() => this.setState( { showJsonEditor : false } )}
            value={attribute.metadata} />
        </Flex>
        
      </Section>
    ) 
  }
}
